<template>
    <div v-if="static" class="static-page px-0 pt-3 pb-0">
        <v-card class="pa-2" tile flat>
            <v-row>
                <!-- back btn -->
                <v-col cols="12" class="py-0">
                    <div class="d-flex flex-row align-center mb-2">
                        <back-button/>
                    </div>
                </v-col>
                <!-- back btn -->

                <!-- don't show intro page again btn -->
                <v-col cols="12" v-if="static.slug === 'intro'" class="py-0 ">
                    <div class="d-flex align-center px-2 mb-3" id="btn-stop-intropage">
                        <template v-if="currentUser && !currentUser.intro">
                            <v-btn outlined block
                                   @click="stopIntroRedirects">
                                {{ $vuetify.lang.t('$vuetify.buttons.stop_intro_redirect') }}
                            </v-btn>
                        </template>
                        <template v-else>
                            <v-icon>mdi-check-circle-outline</v-icon>
                            <div class="ml-1">
                                {{ $vuetify.lang.t('$vuetify.buttons.intro_redirect_stop') }}
                            </div>
                        </template>
                    </div>
                </v-col>
                <!-- /don't show intro page again btn -->

                <!-- page image -->
                <v-col cols="12" sm="5" class="py-0">
                    <v-img v-if="static.hero_image"
                           :src="static.hero_image"
                           squared
                           class="mb-4"></v-img>
                </v-col>
                <!-- /page image -->

                <!-- page content -->
                <v-col cols="12" sm="7" class="py-0">
                    <anchor-content :content="static.content" class="mx-4"></anchor-content>
<!--                    <div v-html="static.content" class="mx-4 rtecontent"></div>-->
                    <!-- guided tour only on intro page -->
                    <template v-if="static.slug === 'intro'" class="py-0">
                        <div class="d-flex align-center px-2 mb-3">
                            <v-btn outlined class="accent" block id="btn-start-tour"
                                   @click="enableTours">
                                {{ $vuetify.lang.t('$vuetify.guidedtour.start_button') }}
                            </v-btn>
                        </div>
                    </template>
                    <!-- /guided tour only on intro page -->
                </v-col>
                <!-- /page content -->


            </v-row>
        </v-card>

        <!-- static page gallery -->
        <v-row class="mt-5">
            <v-col cols="12" sm="5" class="py-0" v-if="static.downloads && static.downloads.length > 0">
                <heading-section id="downloads" class="d-flex">
                    <div v-if="static.custom_downloads_headline">{{ static.custom_downloads_headline }}</div>
                    <div v-else>{{ $vuetify.lang.t('$vuetify.title.downloads') }}</div>
                </heading-section>
                <v-card class="mt-2 pa-2" flat tile>
                    <v-list two-line>
                        <v-list-item @click="downloadHandler(download.source, download.id)"
                                     v-for="download in static.downloads"
                                     :key="download.id"
                                     :disabled="$wait.is(`download-${download.id}`)"
                                     :loading="$wait.is(`download-${download.id}`)">
                            <v-list-item-icon>
                                <v-icon>mdi-file-download-outline</v-icon>
                            </v-list-item-icon>

                            <v-list-item-content>
                                <v-list-item-title class="subtitle-1 font-weight-bold">{{ download.name }}</v-list-item-title>
                                <v-list-item-subtitle class="subtitle-1">{{ download.description }}</v-list-item-subtitle>
                            </v-list-item-content>

                            <v-list-item-icon>
                                <v-icon>mdi-download</v-icon>
                            </v-list-item-icon>
                        </v-list-item>
                    </v-list>
                </v-card>
            </v-col>


            <v-col v-if="static.gallery && static.gallery.length > 0"
                   cols="12"
                   sm="7"
                   class="py-0"
                   :class="{ 'mt-5 mt-sm-0' : static.downloads && static.downloads.length > 0}">
                <heading-section id="gallery" class="d-flex">
                    <div v-if="static.custom_gallery_headline">{{ static.custom_gallery_headline }}</div>
                    <div v-else>{{ $vuetify.lang.t('$vuetify.title.news_slider') }}</div>
                </heading-section>
                <v-card class="pa-2" flat tile>
                    <div class="position-relative">
                        <v-carousel pa-0
                                    height="auto"
                                    hide-delimiters
                                    :show-arrows="false"
                                    v-model="carouselIndex">
                            <v-carousel-item
                                    v-for="image in static.gallery"
                                    :key="image.id">
                                <v-img :src="image.source" aspect-ratio="1"></v-img>
                            </v-carousel-item>
                            <div class="v-carousel__controls"
                                 v-if="static.gallery && static.gallery.length > 1">
                                <v-icon @click="carouselIndex--" :disabled="!(carouselIndex > 0)">
                                    mdi-chevron-left
                                </v-icon>
                                {{ `${carouselIndex+1}/${static.gallery.length}` }}
                                <v-icon @click="carouselIndex++" :disabled="!(carouselIndex < static.gallery && static.gallery.length - 1)">
                                    mdi-chevron-right
                                </v-icon>
                            </div>
                        </v-carousel>
                        <v-expand-transition>
                            <div v-if="static.gallery[carouselIndex].description">
                                <v-scroll-y-transition mode="out-in">
                                    <p class="px-5 pt-4 pb-2 mb-0 body-1" :key="static.gallery[carouselIndex].id">
                                        {{ static.gallery[carouselIndex].description }}
                                    </p>
                                </v-scroll-y-transition>
                            </div>
                        </v-expand-transition>
                    </div>
                </v-card>
            </v-col>
        </v-row>
        <!-- /static page gallery -->
    </div>

    <!-- page error -->
    <v-container v-else>
        <page-not-found/>
    </v-container>
    <!-- /page error -->
</template>

<script>
    import {
        mapActions,
        mapState
    } from 'vuex';
    // components
    import AnchorContent from '../components/AnchorContent';
    import BackButton from '../components/BackButton';
    import HeadingSection from '../components/HeadingSection';
    import PageNotFound from '../components/PageNotFound';
    // models
    import StaticPage from '../models/StaticPage';
    import User from '../models/User';
    // utils
    import {
        checkDownloadPermission,
        setPageTitle,
    } from '../utils/helper';

    export default {
        name: 'static-page',
        props: ['slug'],
        data() {
            return {
                carouselIndex: 0,
            }
        },
        watch: {
            slug(value) {
                this.stopTour();
                if (value === 'intro') {
                    this.initTour();
                } else {
                    this.$vuetify.goTo(0);
                }
            }
        },
        components: {
            AnchorContent,
            BackButton,
            HeadingSection,
            PageNotFound,
        },
        computed: {
            ...mapState([
                'tours',
                'tourIntro',
            ]),
            static() {
                return StaticPage.getBySlug(this.slug);
            },
            notFoundTitle() {
                return this.$vuetify.lang.t('$vuetify.page_not_found.page_title');
            },
            currentUser() {
                return User.getCurrent();
            },
        },
        methods: {
            ...mapActions([
                'set',
            ]),
            async downloadHandler(url, id) {
                return checkDownloadPermission(url, id);
            },
            async enableTours() {
                this.set({ item: 'tourIntro', value: false });
                this.set({ item: 'tourDashboard', value: false });
                this.set({ item: 'tourShop', value: false });
                await this.set({ item: 'tours', value: false });
                await this.set({ item: 'tours', value: true });
                this.$tours['introTour'].start();
            },
            stopIntroRedirects() {
                return User.updateIntro();
            },
            initTour() {
                if (this.tours && !this.tourIntro && this.slug === 'intro') {
                    this.$tours['introTour'].start();
                }
            },
            stopTour() {
                if (this.$tours['introTour'] && this.$tours['introTour'].isRunning) {
                    this.$tours['introTour'].stop();
                }
            }
        },
        mounted() {
            this.$nextTick(() => {
                this.initTour();
            });
            // Product.fetch();
        },
        beforeDestroy() {
            this.stopTour();
        },
    }
</script>
